import React, { useState, useEffect } from 'react';

const CauseModal = ({ 
  showModal, 
  onClose, 
  onSave, 
  handbooks,
  selectedCauses = [] // Массив ID выбранных причин
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  // id причины, которую только что отметили: на один прогон анимации подъёма
  const [justMovedId, setJustMovedId] = useState(null);

  // Инициализируем выбранные причины при открытии модального окна
  useEffect(() => {
    if (showModal) {
      // Нормализуем ID (приводим к числам для единообразия)
      const normalizedCauses = (selectedCauses || []).map(id => {
        return typeof id === 'string' ? parseInt(id, 10) : id;
      }).filter(id => !isNaN(id));
      setSelectedIds(normalizedCauses);
      setSearch('');
      setJustMovedId(null);
    }
  }, [showModal, selectedCauses]);

  if (!showModal) return null;

  // Получаем справочник причин (ID 10)
  const handbook = handbooks?.find(h => h.id === 10);
  if (!handbook || !handbook.references) {
    return null;
  }

  // Фильтруем причины с типом LEAF_EATING
  const allCauses = handbook.references
    .filter(ref => ref.type === 'LEAF_EATING')
    .map(r => ({
      id: r.id,
      name: r.name,
      code: r.code || null,
      description: r.description || null
    }));

  // Фильтруем по поисковому запросу
  const filteredCauses = allCauses.filter(cause => {
    const searchLower = search.toLowerCase();
    return cause.name.toLowerCase().includes(searchLower) ||
           (cause.code && String(cause.code).toLowerCase().includes(searchLower)) ||
           (cause.description && cause.description.toLowerCase().includes(searchLower));
  });

  const toNumericId = (id) => (typeof id === 'string' ? parseInt(id, 10) : id);
  const selectedIdsNormalized = selectedIds.map(toNumericId);
  const isCauseSelected = (cause) => selectedIdsNormalized.includes(toNumericId(cause.id));

  // Выбранные причины выносим отдельным разделом наверх: их не больше трёх, а
  // справочник длинный — иначе отмеченное теряется из виду. Внутри каждого
  // раздела порядок справочника сохраняется.
  const chosenCauses = filteredCauses.filter(isCauseSelected);
  const otherCauses = filteredCauses.filter(cause => !isCauseSelected(cause));
  const hasVisibleCauses = filteredCauses.length > 0;

  // Обработка клика по причине
  const handleCauseClick = (causeId) => {
    // Строка переедет в раздел «Выбранные» — отметим её для анимации подъёма.
    // Считаем это здесь, а не внутри setSelectedIds: обновляющая функция должна
    // оставаться чистой, в StrictMode она вызывается дважды.
    const clickedId = toNumericId(causeId);
    if (!selectedIdsNormalized.includes(clickedId) && selectedIds.length < 3) {
      setJustMovedId(clickedId);
    }

    setSelectedIds(prev => {
      // Приводим ID к числу для сравнения
      const causeIdNum = typeof causeId === 'string' ? parseInt(causeId, 10) : causeId;
      const prevNormalized = prev.map(id => typeof id === 'string' ? parseInt(id, 10) : id);
      
      if (prevNormalized.includes(causeIdNum)) {
        // Удаляем, если уже выбрана
        return prev.filter(id => {
          const idNum = typeof id === 'string' ? parseInt(id, 10) : id;
          return idNum !== causeIdNum;
        });
      } else {
        // Добавляем, если не выбрана и не превышен лимит
        if (prev.length < 3) {
          return [...prev, causeId];
        } else {
          alert('Можно выбрать не более 3-х причин на породу');
          return prev;
        }
      }
    });
  };

  // Обработка сохранения
  const handleSave = () => {
    // Нормализуем ID (приводим к числам для единообразия)
    const normalizedIds = selectedIds.map(id => {
      return typeof id === 'string' ? parseInt(id, 10) : id;
    }).filter(id => !isNaN(id));
    onSave(normalizedIds);
    onClose();
  };

  // Обработка отмены
  const handleCancel = () => {
    // Восстанавливаем исходные выбранные причины
    const normalizedCauses = (selectedCauses || []).map(id => {
      return typeof id === 'string' ? parseInt(id, 10) : id;
    }).filter(id => !isNaN(id));
    setSelectedIds(normalizedCauses);
    onClose();
  };

  const renderSectionRow = (title) => (
    <tr key={`section-${title}`} style={{ backgroundColor: '#f1f3f5' }}>
      <td
        colSpan={2}
        style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 600, color: '#495057' }}
      >
        {title}
      </td>
    </tr>
  );

  const renderCauseRow = (cause) => {
    const isSelected = isCauseSelected(cause);
    const canSelect = selectedIds.length < 3 || isSelected;

    const justMoved = isSelected && toNumericId(cause.id) === justMovedId;

    return (
      <tr
        key={cause.id}
        className={`${isSelected ? 'table-active' : ''}${justMoved ? ' cause-row-moved' : ''}`.trim()}
        onAnimationEnd={() => { if (justMoved) { setJustMovedId(null); } }}
        onClick={() => canSelect && handleCauseClick(cause.id)}
        style={{
          cursor: canSelect ? 'pointer' : 'not-allowed',
          opacity: canSelect ? 1 : 0.6
        }}
      >
        <td style={{ width: '40px', textAlign: 'center', verticalAlign: 'middle' }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {}}
            onClick={(e) => {
              e.stopPropagation();
              if (canSelect) {
                handleCauseClick(cause.id);
              }
            }}
            onFocus={(e) => e.target.blur()}
            style={{
              cursor: canSelect ? 'pointer' : 'not-allowed',
              outline: 'none',
              boxShadow: 'none'
            }}
            disabled={!canSelect}
          />
        </td>
        <td style={{ verticalAlign: 'middle' }}>
          <div className="picker-item-label" style={{ fontWeight: 'normal' }}>
            {cause.code ? `${cause.name} (${cause.code})` : cause.name}
          </div>
          {cause.description && (
            <div className="picker-item-description" style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
              {cause.description}
            </div>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="picker-overlay" onClick={handleCancel}>
      <div className="picker-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
        <div className="picker-header">
          <h3>Выберите причину (до 3-х на породу)</h3>
          <button className="picker-close" onClick={handleCancel}>×</button>
        </div>
        
        <div className="picker-search">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск..."
            className="form-control"
            autoFocus
          />
        </div>

        <div style={{ padding: '10px', fontSize: '12px', color: '#6c757d' }}>
          Выбрано: {selectedIds.length} из 3
        </div>
        
        <div className="picker-content" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {!hasVisibleCauses ? (
            <div style={{ padding: '15px', textAlign: 'center', color: '#6c757d' }}>
              {search ? 'Ничего не найдено' : 'Нет доступных причин с типом LEAF_EATING'}
            </div>
          ) : (
            <table className="table" style={{ marginBottom: 0, width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center' }}></th>
                  <th style={{ textAlign: 'left' }}>Причина</th>
                </tr>
              </thead>
              <tbody>
                {chosenCauses.length > 0 && renderSectionRow('Выбранные')}
                {chosenCauses.map(renderCauseRow)}
                {chosenCauses.length > 0 && otherCauses.length > 0 && renderSectionRow('На выбор')}
                {otherCauses.map(renderCauseRow)}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ 
          padding: '15px', 
          borderTop: '1px solid #dee2e6', 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '10px' 
        }}>
          <button 
            className="btn btn-secondary" 
            onClick={handleCancel}
            style={{ minWidth: '100px' }}
          >
            Отмена
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSave}
            style={{ minWidth: '100px' }}
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
};

export default CauseModal;
